import { z } from 'zod';
import {
  mySubdivisionSchema,
  subdivisionStatsSchema,
  subdivisionMovementRowSchema,
  subdivisionSchema,
  storeroomSchema,
  subdivisionStockRowSchema,
  type CreateStoreroomInput,
  type CreateSubdivisionInput,
  type MoveLotInput,
  type StoreroomDto,
  type UpdateStoreroomInput,
  type MySubdivisionDto,
  type SubdivisionStatsDto,
  type SubdivisionDto,
  type UpdateSubdivisionInput,
} from '@eombor/contracts';
import { apiClient } from '../../auth/model/auth.store.js';
import type { ApiClient } from '../../../shared/api/client.js';

/**
 * Backend javoblarining ish vaqtidagi sxemasi. Manba:
 *   `subdivision/infrastructure/http/subdivision.controller.ts`
 *
 * Bo'linma har doim EGASI bilan keladi (`orgUnitName`): ro'yxatda «R va AX»
 * yolg'iz ko'rsatilmaydi — u bir necha korxonada bir xil nomda uchraydi.
 */
export const subdivisionPageSchema = z.object({
  items: z.array(subdivisionSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export const subdivisionStockPageSchema = z.object({
  items: z.array(subdivisionStockRowSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export const subdivisionMovementPageSchema = z.object({
  items: z.array(subdivisionMovementRowSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export interface SubdivisionQuery {
  orgUnitId?: string;
  mtuId?: string;
  search?: string;
  activeOnly?: boolean;
  page?: number;
  limit?: number;
}

export interface SubdivisionApi {
  list: (query?: SubdivisionQuery) => Promise<z.infer<typeof subdivisionPageSchema>>;
  get: (id: string) => Promise<SubdivisionDto>;
  create: (input: CreateSubdivisionInput) => Promise<SubdivisionDto>;
  update: (id: string, input: UpdateSubdivisionInput) => Promise<SubdivisionDto>;
  /**
   * O'CHIRISH — server FAQAT superadminga ruxsat beradi va faqat
   * ishlatilmagan bo'linmani o'chiradi (403 / 409).
   */
  remove: (id: string) => Promise<void>;
  stock: (
    id: string,
    query?: { search?: string; page?: number; limit?: number },
  ) => Promise<z.infer<typeof subdivisionStockPageSchema>>;

  /**
   * «MENING OMBORIM» — uch so'rov, hammasi xodimning O'Z bo'linmasi haqida.
   *
   * Yo'lda `id` YO'Q va bu ataylab: uni server xodimning aktiv ish joyidan
   * oladi. Mijozda `id` bo'lsa, u bir kun boshqasini yozib ko'rish uchun
   * ishlatilardi va endpoint doira tekshiruviga muhtoj bo'lib qolardi.
   */
  mine: () => Promise<MySubdivisionDto>;
  /** Panel ko'rsatkichlari — `mine` dan alohida, chunki og'irroq. */
  mineStats: () => Promise<SubdivisionStatsDto>;
  mineStock: (
    query?: { search?: string; storeroomId?: string; page?: number; limit?: number },
  ) => Promise<z.infer<typeof subdivisionStockPageSchema>>;
  storerooms: () => Promise<StoreroomDto[]>;
  createStoreroom: (input: CreateStoreroomInput) => Promise<StoreroomDto>;
  updateStoreroom: (id: string, input: UpdateStoreroomInput) => Promise<StoreroomDto>;
  addZone: (id: string, name: string) => Promise<StoreroomDto>;
  removeZone: (id: string, zoneId: string) => Promise<StoreroomDto>;
  moveLot: (lotId: string, input: MoveLotInput) => Promise<void>;
  mineMovements: (
    query?: { page?: number; limit?: number },
  ) => Promise<z.infer<typeof subdivisionMovementPageSchema>>;
}

/** Sahifalash va qidiruv — uch joyda bir xil shakl. */
const pageParams = (query: { search?: string; storeroomId?: string; page?: number; limit?: number }): string => {
  const params = new URLSearchParams({
    page: String(query.page ?? 1),
    limit: String(query.limit ?? 50),
  });
  if (query.search !== undefined && query.search !== '') params.set('search', query.search);
  if (query.storeroomId !== undefined) params.set('storeroomId', query.storeroomId);
  return params.toString();
};

const storeroomListSchema = z.array(storeroomSchema);

const queryString = (query: SubdivisionQuery): string => {
  const params = new URLSearchParams({
    page: String(query.page ?? 1),
    limit: String(query.limit ?? 200),
  });
  if (query.orgUnitId !== undefined) params.set('orgUnitId', query.orgUnitId);
  if (query.mtuId !== undefined) params.set('mtuId', query.mtuId);
  if (query.search !== undefined && query.search !== '') params.set('search', query.search);
  if (query.activeOnly === true) params.set('activeOnly', 'true');
  return params.toString();
};

export const createSubdivisionApi = (client: ApiClient): SubdivisionApi => ({
  list: (query = {}) =>
    client.apiFetch(`/subdivisions?${queryString(query)}`, { schema: subdivisionPageSchema }),

  get: (id) => client.apiFetch(`/subdivisions/${id}`, { schema: subdivisionSchema }),

  create: (input) =>
    client.apiFetch('/subdivisions', {
      method: 'POST',
      body: JSON.stringify(input),
      schema: subdivisionSchema,
    }),

  update: (id, input) =>
    client.apiFetch(`/subdivisions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
      schema: subdivisionSchema,
    }),

  remove: async (id) => {
    // 204 — tanasiz javob, shuning uchun sxema yo'q.
    await client.apiFetch(`/subdivisions/${id}`, { method: 'DELETE' });
  },

  stock: (id, query = {}) =>
    client.apiFetch(`/subdivisions/${id}/stock?${pageParams(query)}`, {
      schema: subdivisionStockPageSchema,
    }),

  mine: () => client.apiFetch('/subdivisions/mine', { schema: mySubdivisionSchema }),

  mineStats: () =>
    client.apiFetch('/subdivisions/mine/stats', { schema: subdivisionStatsSchema }),

  mineStock: (query = {}) =>
    client.apiFetch(`/subdivisions/mine/stock?${pageParams(query)}`, {
      schema: subdivisionStockPageSchema,
    }),

  storerooms: () => client.apiFetch('/subdivisions/mine/storerooms', { schema: storeroomListSchema }),

  createStoreroom: (input) =>
    client.apiFetch('/subdivisions/mine/storerooms', {
      method: 'POST',
      body: JSON.stringify(input),
      schema: storeroomSchema,
    }),

  updateStoreroom: (id, input) =>
    client.apiFetch(`/subdivisions/mine/storerooms/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
      schema: storeroomSchema,
    }),

  addZone: (id, name) =>
    client.apiFetch(`/subdivisions/mine/storerooms/${id}/zones`, {
      method: 'POST',
      body: JSON.stringify({ name }),
      schema: storeroomSchema,
    }),

  removeZone: (id, zoneId) =>
    client.apiFetch(`/subdivisions/mine/storerooms/${id}/zones/${zoneId}`, {
      method: 'DELETE',
      schema: storeroomSchema,
    }),

  moveLot: async (lotId, input) => {
    await client.apiFetch(`/subdivisions/mine/lots/${lotId}/move`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  mineMovements: (query = {}) =>
    client.apiFetch(`/subdivisions/mine/movements?${pageParams(query)}`, {
      schema: subdivisionMovementPageSchema,
    }),
});

/** Ilovaning yagona klienti ustidagi tayyor ko'rinish. */
export const subdivisionApi = createSubdivisionApi(apiClient);
